import { Builder } from '@builder.io/react';
import React, { useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { lazyLoadDefaultConfig } from '../../../constants';
import {
  generateTestimonialsSchema,
  type TestimonialSchemaInput,
} from '../../../utils/structured-data/testimonialsSchemaGenerator';
import { hasValidTextContent } from '../../../utils/textUtils';
import StructuredData from '../../elements/StructuredData';
import BuilderSection from '../../layouts/BuilderSection';
import { Testimonial } from './Testimonial.type';
import { TestimonialCard } from './TestimonialCard';
import { HeroTestimonial } from './TestimonialHero';

interface TestimonialsV2Props {
  variant: '1-column' | '3-column';
  heading?: string;
  subheading?: string;
  backgroundColor?: string;
  testimonials: Testimonial[];
}

const TestimonialsV2 = ({
  variant,
  heading,
  subheading,
  backgroundColor = '',
  testimonials,
}: TestimonialsV2Props) => {
  const validSubheading =
    subheading && hasValidTextContent(subheading) ? subheading : undefined;
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [width, setWidth] = React.useState(0);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || variant === '1-column') return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.target !== container) return;

      const scrollAmount = 300;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
          break;
        case 'ArrowRight':
          event.preventDefault();
          container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
          break;
        case 'Home':
          event.preventDefault();
          container.scrollTo({ left: 0, behavior: 'smooth' });
          break;
        case 'End':
          event.preventDefault();
          container.scrollTo({
            left: container.scrollWidth,
            behavior: 'smooth',
          });
          break;
      }
    };

    container.addEventListener('keydown', handleKeyDown);
    return () => container.removeEventListener('keydown', handleKeyDown);
  }, [variant]);

  const schemaData = React.useMemo(() => {
    if (!testimonials?.length) return [];

    const schemaInput: TestimonialSchemaInput[] = testimonials.map(
      (testimonial) => ({
        name: testimonial.name,
        title: testimonial.title,
        quote: testimonial.quote,
        image: testimonial.image ? `${testimonial.image}` : undefined,
        imageAlt: testimonial.imageAlt,
      })
    );

    return generateTestimonialsSchema({
      testimonials: schemaInput,
      organizationName: 'Storyblocks',
      organizationUrl: 'https://www.storyblocks.com',
    });
  }, [testimonials]);

  return (
    <>
      {schemaData.map((schema, index) => (
        <StructuredData key={`testimonial-schema-${index}`} data={schema} />
      ))}

      <section
        style={{ backgroundColor }}
        aria-label={
          heading
            ? `${heading} - Customer testimonials`
            : 'Customer testimonials'
        }
      >
        <BuilderSection
          heading={heading}
          subheading={validSubheading}
          className="items-center w-screen md:w-full"
          headingClassNames="lg:text-left text-2xl lg:text-4xl text-gray-900 font-bold font-polysans"
        >
          {variant === '1-column' ? (
            testimonials?.length > 0 && (
              <HeroTestimonial testimonial={testimonials[0]} />
            )
          ) : (
            <div className="w-full px-4">
              <div
                ref={scrollContainerRef}
                className={twMerge(
                  'flex gap-6 overflow-x-auto pb-4 -mx-4 px-4 snap-x snap-mandatory',
                  'md:flex-col md:gap-8 md:overflow-visible md:pb-0 md:mx-0 md:px-0 md:snap-none',
                  'lg:flex lg:[&>*]:w-[calc(33.3%-22px)] lg:gap-8 lg:flex-row lg:items-stretch lg:overflow-x-auto',
                  'scrollbar-hide [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  'rounded-lg',
                  testimonials?.length < 3 && 'lg:justify-center'
                )}
                role="group"
                aria-label="Customer testimonials carousel"
                aria-describedby="testimonials-instructions"
              >
                <div
                  id="testimonials-instructions"
                  className="sr-only"
                  aria-live="polite"
                >
                  Use arrow keys to navigate through testimonials. Press Home to
                  go to first testimonial, End to go to last testimonial.
                </div>
                {testimonials?.map((testimonial, index) => (
                  <div
                    key={`testimonial-${index}`}
                    className="flex-shrink-0 snap-start flex flex-col lg:items-stretch"
                    style={{
                      width:
                        width >= 1024
                          ? 'calc(33.3% - 22px)'
                          : width >= 672
                          ? 'auto'
                          : 'calc(100vw - 96px)',
                    }}
                    role="listitem"
                    aria-posinset={index + 1}
                    aria-setsize={testimonials.length}
                  >
                    <TestimonialCard {...testimonial} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </BuilderSection>
      </section>
    </>
  );
};

Builder.registerComponent(TestimonialsV2, {
  name: 'Testimonials V2',
  inputs: [
    {
      name: 'variant',
      type: 'string',
      enum: ['1-column', '3-column'],
      required: true,
      defaultValue: '3-column',
      friendlyName: 'Layout Variant',
      helperText:
        'Choose between a hero-style single testimonial or a 3-column grid layout',
    },
    {
      name: 'heading',
      type: 'string',
      required: false,
      defaultValue: '',
      friendlyName: 'Headline (Optional)',
      helperText: 'Optional headline for the testimonials section',
    },
    {
      name: 'subheading',
      type: 'richText',
      required: false,
      defaultValue: '',
      friendlyName: 'Subheading (Optional)',
      helperText: 'Optional subheading paragraph',
      onChange: (options) => {
        const characterLimit = 300;
        const subheadingText = options.get('subheading');
        const htmlTagRegex = /<.*?>/gi;

        const textOnly = subheadingText.replaceAll(htmlTagRegex, '');
        if (textOnly.length <= characterLimit) {
          return;
        }

        const tokenizedTags = [...subheadingText.matchAll(htmlTagRegex)].map(
          (item) => item[0]
        );
        const tokenizedText = subheadingText.split(htmlTagRegex);

        const trimmedTextNodes = tokenizedText.reduce((textNodes, text) => {
          const currentTextLength = textNodes.join('').length;
          return currentTextLength + text.length <= characterLimit
            ? textNodes.concat(text)
            : textNodes.concat(
                text.slice(0, characterLimit - currentTextLength)
              );
        }, []);

        const trimmedRichText = tokenizedTags
          .map((tag, i) => trimmedTextNodes[i] + tag)
          .join('');

        alert(`Max of ${characterLimit} characters allowed.`);
        options.set('subheading', trimmedRichText);
      },
    },
    {
      name: 'backgroundColor',
      type: 'color',
      required: false,
      defaultValue: '',
      friendlyName: 'Background Color',
      helperText: 'Choose a background color',
    },
    {
      name: 'testimonials',
      type: 'list',
      required: true,
      friendlyName: 'Testimonials',
      helperText: 'Add testimonials. Maximum of 6 testimonials allowed.',
      defaultValue: [
        {
          name: 'Cody Wanner',
          title: 'YouTuber & Content Creator',
          quote:
            'Whether it was necessary but hard-to-capture-myself stock for a client video, or some beautiful aerial for a YouTube video, I have used Storyblocks to create compelling, emotional, and impactful content.',
          imageAlt: 'Photo of Cody Wanner',
          lazyLoadImage: true,
        },
        {
          name: 'Currently Hannah',
          title: 'YouTuber',
          quote:
            "It's so rare to find a subscription service that covers not just videos but music, sound effects, editing tools, and many more. Such a valuable tool for video production!",
          imageAlt: 'Photo of Currently Hannah',
          lazyLoadImage: true,
        },
        {
          name: 'Bryan Francisco',
          title: 'Video Creator',
          quote:
            'I love having instant access to these amazing clips to use in my video projects! Knowing I have unlimited downloads - it lets me test out different clip types to use for my projects!',
          imageAlt: 'Photo of Bryan Francisco',
          lazyLoadImage: true,
        },
      ],
      onChange: (options) => {
        const maxAllowed = 6;
        if (options.get('testimonials').length > maxAllowed) {
          options.set(
            'testimonials',
            options.get('testimonials').slice(0, maxAllowed)
          );
          alert(
            `Maximum number of testimonials for 3-column variant is ${maxAllowed}.`
          );
        }
      },
      subFields: [
        {
          name: 'name',
          type: 'string',
          required: true,
          defaultValue: '[Name]',
          friendlyName: 'Name',
          helperText: 'The name of the person giving the testimonial',
        },
        {
          name: 'title',
          type: 'string',
          required: false,
          defaultValue: '',
          friendlyName: 'Title/Company (Optional)',
          helperText: 'Job title or company name',
        },
        {
          name: 'quote',
          type: 'longText',
          required: true,
          defaultValue: '[Quote]',
          friendlyName: 'Quote Text',
          helperText: 'The testimonial quote',
        },
        {
          name: 'date',
          type: 'date',
          required: false,
          defaultValue: '',
          friendlyName: 'Date (Optional)',
          helperText: 'Date of the testimonial, for structured data purposes.',
        },
        {
          name: 'image',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
          required: true,
          friendlyName: 'Image',
        },
        {
          name: 'imageAlt',
          type: 'string',
          required: true,
          defaultValue: '',
          friendlyName: 'Image Alt Text',
          helperText: 'Alternative text for the image for accessibility',
        },
        {
          ...lazyLoadDefaultConfig,
        },
        {
          name: 'link',
          type: 'url',
          required: false,
          defaultValue: '',
          friendlyName: 'Link (Optional)',
          helperText:
            'Optional link to make the testimonial clickable. Can be internal (/path) or external (https://...)',
        },
      ],
    },
  ],
});
